/**
 * Create Challenge Page Types
 * Type definitions for the challenge creation flow
 */

import type { TeamKitType } from '../../../constants/teamKitColors';

/** Subset of API odds.markets[] (list or details) for guest slip snapshot + Monkey preview. */
export interface OddsMarketSnapshot {
  id: number;
  name?: string;
  values: Array<{ value: string; odd: number }>;
}

/** Kit config attached by API from teams table (0 extra client lookups). */
export interface GameKitConfig {
  primary: string;
  secondary: string;
  type: TeamKitType;
}

export interface Game {
  id: string;
  /** Sport key (specs/multi-sport-challenges). Absent on older cached payloads = football. */
  sport?: string;
  homeTeam: string;
  awayTeam: string;
  league: string;
  /** League id from API; used for favorites and stable sort. */
  leagueId?: string;
  /** ISO-style code for flagcdn (from league.country + league.name via resolveLeagueCountryCodeForFlag). */
  leagueCountryCode?: string;
  /** League displayOrder from API; lower = more popular. Used for sort when not favorite. */
  leagueDisplayOrder?: number;
  date: string;
  time: string;
  /** Raw date from API (ISO or YYYY-MM-DD) so the card can derive time like MatchDetailsScreen when time is invalid. */
  rawDate?: string;
  /**
   * Headline outcome prices.
   *
   * `home`/`away` are null for a FIELD sport (specs/formula-one-challenges): a Grand Prix has no
   * two sides, so there is no home or away price to show. They are deliberately nullable rather
   * than defaulted, because the previous `?? 2.0` fallback would have stamped a fabricated 2.00
   * onto every race — visible as a placeholder price row, and worse, storable on a pick.
   *
   * Read `fieldOutcomeCount` instead to know a race is priced; the prices themselves are the
   * per-driver values in `oddsMarkets`.
   */
  odds: {
    home: number | null;
    away: number | null;
    draw?: number;
    /** FIELD sports only — how many entrants are priced in the primary market. */
    fieldOutcomeCount?: number;
  };
  /** From API when match list includes homeKit/awayKit (batch join). */
  homeKit?: GameKitConfig;
  awayKit?: GameKitConfig;
  /** Total selectable pick outcomes for this match (sum of market values). Set at transform; fallback when no markets. */
  availablePicksCount: number;
  /** True when server returned an Odds row; false when odds were null (client then uses 2.0/2.0/3.0 fallback). Used to hide games without real odds in create flow. */
  hasRealOdds?: boolean;
  /** True when the Odds row is synthetic (estimated by us, no bookmaker odds). Surfaced as an "Estimated" badge. */
  oddsAreEstimated?: boolean;
  /** Present when matches list (or details) included odds.markets — needed for guest Monkey preview on non-1X2. */
  oddsMarkets?: OddsMarketSnapshot[];
  /** FIELD sports only — the event's own name, e.g. "Netherlands Grand Prix". */
  eventName?: string;
  /** FIELD sports only — where it is held, e.g. "Circuit Zandvoort". */
  venue?: string;
}

export interface Friend {
  id: string;
  name: string;
  avatar: string;
  coins?: number;
  isSelected?: boolean;
}

/** Match-winner slip (1X2). When marketId is set, slip is an "other" market selection. */
export interface PickSlip {
  gameId: string;
  prediction?: 'home' | 'away' | 'draw';
  confidence?: 1 | 2 | 3;
  /** Set for "other" markets (Over/Under, BTTS, etc.). When set, value/odd are used. */
  marketId?: number;
  marketName?: string;
  value?: string;
  odd?: number;
  /** Optional display names when slip was added from match details (so Arena can show teams if game not in loaded list). */
  homeTeam?: string;
  awayTeam?: string;
}

/** Type guard: slip is match-winner (1X2) with an explicit pick. Excludes slips with no prediction (e.g. game added only for other picks). */
export const isMatchWinnerSlip = (s: PickSlip): s is PickSlip & { prediction: 'home' | 'draw' | 'away' } =>
  (s.prediction === 'home' || s.prediction === 'draw' || s.prediction === 'away') && s.marketId == null;

/**
 * Rival-first create flow steps (see specs/challenge-flow-redesign).
 * `rival` → pick opponent, `build` → deck/list picks, `review` → slate + stake, `success` → sent.
 * (Replaces the old games→predicting→userGroups order.)
 */
export type CreateStep = 'rival' | 'build' | 'review' | 'success';

/** Build screen view mode. `deck` = one match at a time (default); `list` = all games by date. */
export type BuildView = 'deck' | 'list';

export type ParticipantTab = 'friends' | 'groups';

export interface EditState {
  prediction: 'home' | 'draw' | 'away';
}

/** Single outcome for a market (e.g. "Home" @ 2.10). Aligns with server odds.markets[].values[]. */
export interface MarketOddValue {
  value: string;
  odd: number;
  /** When set (e.g. merged O/U in accept-picks modal), used for submission instead of market.id */
  marketId?: number;
}

/** Canonical name for API/server alignment. Use for odds.markets JSON. */
export type MarketValue = MarketOddValue;

/** Market definition for match details tabs (API-Football v3 ready). */
export interface MarketOdds {
  id: number;
  name: string;
  values: MarketOddValue[];
}

/** Canonical name for API/server alignment. Use for odds.markets JSON array items. */
export type Market = MarketOdds;

/** Filter tab for participants step (friends vs pending). */
export type ParticipantFilterType = 'FRIEND' | 'PENDING';

